import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from "react";
import { FaStore, FaUser, FaHeart, FaShoppingCart, FaBars, FaTimes, FaSearch } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import { useNavigate, Link } from "react-router-dom";
import useAuth from "../../hooks/useAuth.js";
import useCart from "../../hooks/useCart.js";
import useWishlist from "../../hooks/useWishlist.js";
import "./Navbar.css";

import { API_BASE } from "../../config/api";
import {
  DEFAULT_JEWELRY_MENU_GROUPS,
  DEFAULT_JEWELRY_TYPES,
  findJewelryMenuGroupById,
  findJewelryMenuGroupByMaterial,
  normalizeJewelryMenuGroups,
} from "../../utils/materialCatalog";
import { bindStorefrontRefresh } from "../../utils/storefrontRefresh";

// Jewelry menu helpers
const normalizeKey = (s) => String(s || '').trim().toUpperCase();
const chunkItems = (items, size) => {
  const chunks = [];

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }

  return chunks;
};

const SEMI_PRECIOUS_ITEMS_PER_COLUMN = 4;
const SPIRITUAL_MENU_ITEMS = [
  {
    key: "RUDRAKSH",
    label: "RUDRAKSH",
    material: "RUDRAKSH",
  },
  {
    key: "COANCH",
    label: "COANCH",
    children: [
      {
        key: "BLOWING COANCH (SHANKH)",
        label: "BLOWING COANCH (SHANKH)",
        material: "BLOWING COANCH (SHANKH)",
      },
      {
        key: "RIGHT HAND COANCH (DAKSHINAVARTI)",
        label: "RIGHT HAND COANCH (DAKSHINAVARTI)",
        material: "RIGHT HAND COANCH (DAKSHINAVARTI)",
      },
    ],
  },
  {
    key: "ONE EYED COCONUT",
    label: "ONE EYED COCONUT",
    material: "ONE EYE COCONUT (EKAKSHI SHREEFAL)",
  },
];

const findSpiritualMenuItem = (key) => SPIRITUAL_MENU_ITEMS.find((item) => item.key === key) || null;

const toSearchSuggestionQuery = (value = "") => {
  const cleaned = String(value || "")
    .replace(/\([^)]*\)/g, " ")
    .replace(/[-_/]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\bladies\b/g, "")
    .replace(/\bgents\b/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) {
    return "";
  }

  if (cleaned === "ring" || cleaned.endsWith(" ring")) return "rings";
  if (cleaned === "bracelet" || cleaned.endsWith(" bracelet")) return "bracelets";
  if (cleaned === "bangle" || cleaned.endsWith(" bangle")) return "bangles";
  if (cleaned === "anklet" || cleaned.endsWith(" anklet")) return "anklets";

  return cleaned;
};

const buildSearchSuggestions = (items = []) => {
  const seen = new Set();

  return items.reduce((accumulator, item) => {
    const query = toSearchSuggestionQuery(item);

    if (!query || seen.has(query)) {
      return accumulator;
    }

    seen.add(query);
    accumulator.push({
      query,
      label: `Search for ${query}`,
    });

    return accumulator;
  }, []);
};

const cloneJewelryMenuGroups = (groups = DEFAULT_JEWELRY_MENU_GROUPS) =>
  normalizeJewelryMenuGroups(groups).map((group) => ({
    ...group,
    children: Array.isArray(group.children) ? group.children.map((child) => ({ ...child })) : [],
  }));

const findJewelryChildById = (group, childId) =>
  Array.isArray(group?.children) ? group.children.find((child) => child.id === childId) || null : null;

const findJewelryChild = (group, childInput) => {
  const sourceChildren = Array.isArray(group?.children) ? group.children : [];

  if (!sourceChildren.length || childInput == null) {
    return null;
  }

  if (typeof childInput === "string") {
    return sourceChildren.find((child) => (
      child.id === childInput
      || normalizeKey(child.materialValue) === normalizeKey(childInput)
      || normalizeKey(child.label) === normalizeKey(childInput)
    )) || null;
  }

  return findJewelryChildById(group, childInput.id)
    || sourceChildren.find((child) => (
      normalizeKey(child.materialValue) === normalizeKey(childInput.materialValue)
      || normalizeKey(child.label) === normalizeKey(childInput.label)
    ))
    || null;
};

const Navbar = () => {
  const menuRef = useRef(null);
  const jewelryCloseTimeoutRef = useRef(null);
  const gemstoneCloseTimeoutRef = useRef(null);
  const spiritualCloseTimeoutRef = useRef(null);
  const jewelryPanelRef = useRef(null);
  const gemstonePanelRef = useRef(null);
  const spiritualPanelRef = useRef(null);
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { getCartCount } = useCart();
  const { getWishlistCount } = useWishlist();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchPlaceholderIndex, setSearchPlaceholderIndex] = useState(0);
  const [logo, setLogo] = useState({ type: "text", text: "PANCHALI JEWELS", imageUrl: "" });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState({
    jewelry: false,
    gemstones: false,
    spiritual: false
  });
  const [activeMobileJewelryGroupId, setActiveMobileJewelryGroupId] = useState(null);
  const [activeMobileJewelryChildId, setActiveMobileJewelryChildId] = useState(null);
  const [activeMobileSpiritualItem, setActiveMobileSpiritualItem] = useState(null);
  const [isJewelryOpen, setIsJewelryOpen] = useState(false);
  const [activeJewelryGroupId, setActiveJewelryGroupId] = useState(null);
  const [activeJewelryChildId, setActiveJewelryChildId] = useState(null);
  const [isGemstoneOpen, setIsGemstoneOpen] = useState(false);
  const [selectedGemType, setSelectedGemType] = useState(null);
  const [selectedGemstone, setSelectedGemstone] = useState(null);
  const [isSpiritualOpen, setIsSpiritualOpen] = useState(false);
  const [selectedSpiritualItem, setSelectedSpiritualItem] = useState(null);
  const [activeMobileGemType, setActiveMobileGemType] = useState(null);
  const [activeMobileGemstone, setActiveMobileGemstone] = useState(null);
  const [isMoreNoticeOpen, setIsMoreNoticeOpen] = useState(false);
  const [dropdownTop, setDropdownTop] = useState(0);
  const [gemstonePanelMinWidth, setGemstonePanelMinWidth] = useState(null);
  const [dropdownLeft, setDropdownLeft] = useState({
    jewelry: 0,
    gemstones: 0,
    spiritual: 0,
  });
  const [jewelryMenuGroups, setJewelryMenuGroups] = useState(cloneJewelryMenuGroups());
  const [jewelryProducts, setJewelryProducts] = useState(DEFAULT_JEWELRY_TYPES);
  const activeJewelryGroup = findJewelryMenuGroupById(activeJewelryGroupId, jewelryMenuGroups);
  const activeJewelryChild = findJewelryChildById(activeJewelryGroup, activeJewelryChildId);
  const activeMobileJewelryGroup = findJewelryMenuGroupById(activeMobileJewelryGroupId, jewelryMenuGroups);
  const activeMobileJewelryChild = findJewelryChildById(activeMobileJewelryGroup, activeMobileJewelryChildId);
  const activeDesktopJewelryChildren = activeJewelryGroup?.behavior === "nested-materials"
    ? (activeJewelryGroup.children || [])
    : [];
  const activeDesktopJewelryMaterialValue = activeJewelryChild?.materialValue || activeJewelryGroup?.materialValue || "";
  const activeDesktopJewelryGroupLabel = String(activeJewelryGroup?.label || "").toUpperCase();
  const activeDesktopJewelryChildLabel = String(activeJewelryChild?.label || "").toUpperCase();
  const shouldShowDesktopJewelryProducts = activeJewelryGroup?.behavior === "product-types"
    || Boolean(activeJewelryChild);
  const defaultSearchSuggestions = buildSearchSuggestions(jewelryProducts);
  const searchPlaceholder =
    searchTerm.trim() === ""
      ? defaultSearchSuggestions[
          searchPlaceholderIndex % Math.max(defaultSearchSuggestions.length, 1)
        ]?.label || "Search products..."
      : "Search products...";

  const clearJewelryCloseTimeout = () => {
    if (jewelryCloseTimeoutRef.current) {
      clearTimeout(jewelryCloseTimeoutRef.current);
      jewelryCloseTimeoutRef.current = null;
    }
  };

  const clearGemstoneCloseTimeout = () => {
    if (gemstoneCloseTimeoutRef.current) {
      clearTimeout(gemstoneCloseTimeoutRef.current);
      gemstoneCloseTimeoutRef.current = null;
    }
  };

  const clearSpiritualCloseTimeout = () => {
    if (spiritualCloseTimeoutRef.current) {
      clearTimeout(spiritualCloseTimeoutRef.current);
      spiritualCloseTimeoutRef.current = null;
    }
  };

  const clampLeft = (leftX, panelWidth) => {
    const padding = 12;
    const max = Math.max(padding, window.innerWidth - (panelWidth || 0) - padding);
    if (!Number.isFinite(leftX)) return padding;
    return Math.max(padding, Math.min(max, leftX));
  };

  const positionDropdown = (key, triggerEl) => {
    // Avoid re-positioning while already open (prevents "jump" when moving into panel)
    if (key === "jewelry" && isJewelryOpen) return;
    if (key === "gemstones" && isGemstoneOpen) return;
    if (key === "spiritual" && isSpiritualOpen) return;

    const rect = triggerEl.getBoundingClientRect();
    const menuBottom = rect.bottom;

    // Simple positioning with top gap and minimal offsets
    const TOP_GAP = 8;
    const GEMSTONE_LEFT_SHIFT = 30;
    const JEWELRY_CASCADE_WIDTH = 900;
    
    // The dropdown panels use `position: fixed`, so they must be positioned
    // with viewport coordinates from getBoundingClientRect() only.
    setDropdownTop(menuBottom + TOP_GAP);
    
    // Apply positioning based on dropdown type
    if (key === "gemstones") {
      setDropdownLeft((prev) => ({ ...prev, [key]: rect.left - GEMSTONE_LEFT_SHIFT }));
    } else if (key === "spiritual") {
      // Center spiritual dropdown under trigger
      setDropdownLeft((prev) => ({ ...prev, [key]: rect.left + rect.width / 2 }));
    } else if (key === "jewelry") {
      setDropdownLeft((prev) => ({ ...prev, [key]: clampLeft(rect.left, JEWELRY_CASCADE_WIDTH) }));
    } else {
      setDropdownLeft((prev) => ({ ...prev, [key]: rect.left }));
    }
  };

  const clampCenter = (centerX, panelWidth) => {
    const padding = 12;
    const half = Math.max(0, (panelWidth || 0) / 2);
    const min = padding + half;
    const max = window.innerWidth - padding - half;
    if (!Number.isFinite(centerX)) return Math.max(min, Math.min(max, window.innerWidth / 2));
    return Math.max(min, Math.min(max, centerX));
  };

  useLayoutEffect(() => {
    if (isJewelryOpen && jewelryPanelRef.current) {
      const w = jewelryPanelRef.current.getBoundingClientRect().width;
      setDropdownLeft((prev) => ({ ...prev, jewelry: clampLeft(prev.jewelry, w) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJewelryOpen, activeJewelryGroupId, activeJewelryChildId]);

  useLayoutEffect(() => {
    if (isGemstoneOpen && gemstonePanelRef.current) {
      const w = gemstonePanelRef.current.getBoundingClientRect().width;
      if (selectedGemType != null) {
        setGemstonePanelMinWidth((prev) => (prev == null ? w : Math.max(prev, w)));
      }
      setDropdownLeft((prev) => ({ ...prev, gemstones: clampCenter(prev.gemstones, w) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGemstoneOpen, selectedGemType, selectedGemstone]);

  useEffect(() => {
    if (selectedGemType == null) {
      setGemstonePanelMinWidth(null);
    }
  }, [selectedGemType]);

  useLayoutEffect(() => {
    if (isSpiritualOpen && spiritualPanelRef.current) {
      const w = spiritualPanelRef.current.getBoundingClientRect().width;
      setDropdownLeft((prev) => ({ ...prev, spiritual: clampCenter(prev.spiritual, w) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpiritualOpen]);

  const handleUserClick = () => {
  if (loading) return;
  if (user) {
    navigate("/account");
  } else {
    navigate("/login");
  }
};

  const fetchNavbarData = useCallback(() => {
    fetch(`${API_BASE}/site-logo`)
      .then((res) => res.json())
      .then((data) => setLogo(data))
      .catch(() => {});

    fetch(`${API_BASE}/navbar-dropdown`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data.jewelryMenuGroups) && data.jewelryMenuGroups.length) {
          setJewelryMenuGroups(cloneJewelryMenuGroups(data.jewelryMenuGroups));
        }
        if (Array.isArray(data.jewelryTypes) && data.jewelryTypes.length) setJewelryProducts(data.jewelryTypes);
        if (Array.isArray(data.gemstoneTypes) && data.gemstoneTypes.length) setGemStoneTypes(data.gemstoneTypes);
        if (data.gemstoneOptions && typeof data.gemstoneOptions === 'object' && Object.keys(data.gemstoneOptions).length) setGemstoneOptions(data.gemstoneOptions);
        if (Array.isArray(data.gemstoneShapes) && data.gemstoneShapes.length) setGemstoneShapes(data.gemstoneShapes);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchNavbarData();
  }, [fetchNavbarData]);

  useEffect(() => bindStorefrontRefresh(fetchNavbarData, { scopes: ["navigation"] }), [fetchNavbarData]);

  useEffect(() => {
    if (defaultSearchSuggestions.length <= 1) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setSearchPlaceholderIndex((currentIndex) => {
        return (currentIndex + 1) % defaultSearchSuggestions.length;
      });
    }, 2600);

    return () => window.clearInterval(intervalId);
  }, [defaultSearchSuggestions.length]);

  useEffect(() => {
    const submenus = document.querySelectorAll(".subsubsubmenu");
    function handleMouseEnter(event) {
      const submenu = event.currentTarget;
      const rect = submenu.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      if (rect.right > windowWidth) {
        submenu.classList.add("flipped");
      } else {
        submenu.classList.remove("flipped");
      }
    }
    submenus.forEach((submenu) => {
      const parent = submenu.parentElement;
      parent.addEventListener("mouseenter", handleMouseEnter);
    });
  return () => {
      submenus.forEach((submenu) => {
        const parent = submenu.parentElement;
        parent.removeEventListener("mouseenter", handleMouseEnter);
      });
    };
  }, []);

  useEffect(() => {
    if (!isMoreNoticeOpen) {
      document.body.style.removeProperty("overflow");
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsMoreNoticeOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.removeProperty("overflow");
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isMoreNoticeOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmedSearch = searchTerm.trim();
    const queryParams = new URLSearchParams();

    if (trimmedSearch) {
      queryParams.set("search", trimmedSearch);
    }

    navigate(`/store${queryParams.toString() ? `?${queryParams.toString()}` : ""}`);
    setIsMobileSearchOpen(false);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleMobileDropdown = (dropdown) => {
    setIsMobileDropdownOpen((prev) => {
      const nextIsOpen = !prev[dropdown];
      return {
        jewelry: false,
        gemstones: false,
        spiritual: false,
        [dropdown]: nextIsOpen,
      };
    });
  };

  const [gemStoneTypes, setGemStoneTypes] = useState(["PRECIOUS", "SEMI-PRECIOUS", "LGD"]);
  const [gemstoneOptions, setGemstoneOptions] = useState({
    "PRECIOUS": ["Natural Diamonds", "Ruby", "Emerald", "Coral", "Yellow Sapphire", "Blue Sapphire", "Pearls", "Hessonite (Gomed)", "Catseye"],
    "SEMI-PRECIOUS": ["Amethyst", "Peridot", "Topaz", "Turquoise/Firoza", "Malachite", "Lapis lazuli", "Crystal/Sphatik", "Opal", "Citrine/Sunela", "Pink Tourmaline", "Golden pyrite", "Rose Quartz", "Tiger Eye", "Yellow Amber"],
    "LGD": ["Diamonds", "Ruby", "Emerald", "Yellow Sapphire", "Blue Sapphire"],
  });
  const [gemstoneShapes, setGemstoneShapes] = useState(["Rounds", "Emerald cut", "Princess cut", "Cushion cut", "Heart shape", "Marquise", "Pear", "Trillion", "Oval"]);
  const isSemiPreciousGemType = normalizeKey(selectedGemType) === "SEMI-PRECIOUS";
  const selectedGemOptions = gemstoneOptions[selectedGemType] || [];
  const semiPreciousGemColumns = isSemiPreciousGemType
    ? chunkItems(selectedGemOptions, SEMI_PRECIOUS_ITEMS_PER_COLUMN)
    : [];

  const closeGemstoneDropdown = () => {
    setIsGemstoneOpen(false);
    setSelectedGemType(null);
    setSelectedGemstone(null);
    setGemstonePanelMinWidth(null);
  };

  const closeSpiritualDropdown = () => {
    setIsSpiritualOpen(false);
    setSelectedSpiritualItem(null);
  };

  // Desktop jewelry navigation uses hover inside the open dropdown.
  const resetDesktopJewelryPath = () => {
    setActiveJewelryGroupId(null);
    setActiveJewelryChildId(null);
  };

  const closeJewelryDropdown = () => {
    setIsJewelryOpen(false);
    resetDesktopJewelryPath();
  };

  const scheduleJewelryClose = () => {
    clearJewelryCloseTimeout();
    jewelryCloseTimeoutRef.current = setTimeout(() => {
      closeJewelryDropdown();
    }, 160);
  };

  const resolveJewelryGroup = (groupInput) => {
    if (groupInput == null) {
      return null;
    }

    if (typeof groupInput === "string") {
      return findJewelryMenuGroupById(groupInput, jewelryMenuGroups)
        || findJewelryMenuGroupByMaterial(groupInput, jewelryMenuGroups);
    }

    return findJewelryMenuGroupById(groupInput.id, jewelryMenuGroups)
      || findJewelryMenuGroupByMaterial(
        groupInput.materialValue || groupInput.label || groupInput.id || "",
        jewelryMenuGroups
      );
  };

  const previewJewelryGroup = (groupInput) => {
    const nextGroup = resolveJewelryGroup(groupInput);

    if (!nextGroup) {
      return;
    }

    clearJewelryCloseTimeout();

    if (nextGroup.behavior === "direct-material") {
      resetDesktopJewelryPath();
      return;
    }

    setActiveJewelryGroupId(nextGroup.id);
    setActiveJewelryChildId(null);
  };

  const previewJewelryChild = (childInput) => {
    const nextGroup = resolveJewelryGroup(activeJewelryGroup);
    const nextChild = findJewelryChild(nextGroup, childInput);

    if (!nextGroup || !nextChild) {
      return;
    }

    clearJewelryCloseTimeout();
    setActiveJewelryGroupId(nextGroup.id);
    setActiveJewelryChildId(nextChild.id);
  };

  const resetMobileJewelryPath = () => {
    setActiveMobileJewelryGroupId(null);
    setActiveMobileJewelryChildId(null);
  };

  const openMobileJewelryGroup = (groupInput) => {
    const nextGroup = resolveJewelryGroup(groupInput);

    if (!nextGroup) {
      return;
    }

    if (nextGroup.behavior === "direct-material") {
      handleFilterRedirect(nextGroup.materialValue || "", "", "");
      return;
    }

    setActiveMobileJewelryGroupId(nextGroup.id);
    setActiveMobileJewelryChildId(null);
  };

  const openMobileJewelryChild = (childInput) => {
    const nextGroup = resolveJewelryGroup(activeMobileJewelryGroup);
    const nextChild = findJewelryChild(nextGroup, childInput);

    if (!nextGroup || !nextChild) {
      return;
    }

    setActiveMobileJewelryGroupId(nextGroup.id);
    setActiveMobileJewelryChildId(nextChild.id);
  };

  // Close all menus helper (mobile + desktop dropdowns so store page is visible)
  const closeAllMenus = () => {
    setIsMobileMenuOpen(false);
    setIsMobileDropdownOpen({ jewelry: false, gemstones: false, spiritual: false });
    resetMobileJewelryPath();
    setActiveMobileGemType(null);
    setActiveMobileGemstone(null);
    setActiveMobileSpiritualItem(null);
    closeJewelryDropdown();
    setIsGemstoneOpen(false);
    setSelectedGemType(null);
    setSelectedGemstone(null);
    closeSpiritualDropdown();
  };

  // Redirect with jewelry filters
  const handleFilterRedirect = (material, product, price) => {
    const queryParams = new URLSearchParams();
    if (material) queryParams.append("material", material);
    if (product) queryParams.append("product", product);
    if (price) queryParams.append("price", price);
    navigate(`/store?${queryParams.toString()}`);
    closeAllMenus();
  };

  // Redirect with gemstone filters
  const handleGemstoneRedirect = (gemType, gemstone, shape) => {
    const queryParams = new URLSearchParams();
    if (gemType) queryParams.append("material", gemType);
    if (gemstone) queryParams.append("gemstone", gemstone);
    if (shape) queryParams.append("gemstoneShape", shape);
    navigate(`/store?${queryParams.toString()}`);
    closeAllMenus();
  };

  const handleMoreFeatureClick = (event) => {
    event.preventDefault();
    closeAllMenus();
    setIsMobileSearchOpen(false);
    setIsMoreNoticeOpen(true);
  };

  return (
    
    <div className="sticky-navbar-wrapper">
    <header className="navbar-container">
      <div className="announcement-bar">
        <div className="announcement-marquee">
          <div className="announcement-track">
            <span className="announcement-highlight">✦ Welcome to <span className="brand-name-gold">Panchali Jewels</span>! ✦</span>
            <span className="announcement-separator">〰</span>
            <span className="announcement-sub">Your trusted partner in fine jewelry</span>
            <span className="announcement-separator">〰</span>
            <span className="announcement-highlight">✦ Welcome to <span className="brand-name-gold">Panchali Jewels</span>! ✦</span>
            <span className="announcement-separator">〰</span>
            <span className="announcement-sub">Your trusted partner in fine jewelry</span>
            <span className="announcement-separator">〰</span>
          </div>
        </div>
      </div>
        <div className="navbar">
          {/* Mobile Hamburger Menu Button - moved to the beginning */}
          <div className="mobile-menu-toggle" onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </div>

          <Link to="/" className="logo">
            {logo?.type === "image" && logo?.imageUrl?.trim() ? (
              <img src={logo.imageUrl} alt="Logo" className="logo-img" />
            ) : (
              logo?.text || "PANCHALI JEWELS"
            )}
          </Link>

          <form className="search-bar" onSubmit={handleSearch}>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <button type="submit">Search</button>
          </form>

          <div className="icons">
            {/* Mobile Search Icon - only visible on mobile */}
            <div className="icon mobile-search-icon" onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)} style={{ cursor: "pointer" }}>
              <FaSearch />
              <span>Search</span>
            </div>
            
            <Link to="/store" className="icon">
              <FaStore />
              <span>Store</span>
            </Link>
            <div className="icon" onClick={handleUserClick} style={{ cursor: "pointer" }}>
              <FaUser />
              <span>{loading ? "..." : user ? "My Account" : "Login"}</span>
            </div>
            <div className="icon relative" onClick={() => navigate('/wishlist')} style={{ cursor: "pointer" }}>
              <FaHeart />
              <span>Wishlist</span>
              {getWishlistCount() > 0 && (
                <div className="cart-count">{getWishlistCount()}</div>
              )}
            </div>
            <div className="icon relative" onClick={() => navigate('/cart')} style={{ cursor: "pointer" }}>
              <FaShoppingCart />
              <span>Cart</span>
              {getCartCount() > 0 && (
                <div className="cart-count">{getCartCount()}</div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Menu Bar */}
        <nav className="menu-bar desktop-menu" ref={menuRef}>
          <Link to="/" className="menu-item">HOME</Link>
          <Link to="/about" className="menu-item">ABOUT US</Link>

          {/* JEWELRY METAL - dropdown under the link, not full width */}
          <div
            className="menu-item jewelry-two-panel-wrapper"
            onMouseEnter={(e) => {
              clearJewelryCloseTimeout();
              positionDropdown("jewelry", e.currentTarget);
              resetDesktopJewelryPath();
              setIsJewelryOpen(true);
            }}
            onMouseLeave={() => {
              scheduleJewelryClose();
            }}
          >
            <span className="menu-item-label">JEWELRY METAL <IoIosArrowDown size={14} /></span>
            {isJewelryOpen && (
              <div
                className="jewelry-single-panel-container"
                style={{
                  top: dropdownTop,
                  left: dropdownLeft.jewelry,
                }}
                ref={jewelryPanelRef}
                onMouseEnter={clearJewelryCloseTimeout}
                onMouseLeave={scheduleJewelryClose}
              >
                <div className="jewelry-cascade-layout">
                  <div className="jewelry-level-box jewelry-level-box--root">
                    <div className="jewelry-dropdown-heading">
                      <strong>
                        Jewelry Metals and Stones Combinations{" "}
                        <span className="jewelry-heading-note">
                          (LGD - LAB GROWN DIAMONDS) (CZ - Cubic Zirconia)
                        </span>
                      </strong>
                    </div>
                    <div className="jewelry-level-list jewelry-level-list--root">
                      {jewelryMenuGroups.map((group) => {
                        const isActiveGroup = activeJewelryGroup?.id === group.id;
                        const isDirectMaterial = group.behavior === "direct-material";

                        return (
                          <div
                            key={group.id}
                            className={`dropdown-item jewelry-metal-item${isActiveGroup ? " is-active" : ""}`}
                            onMouseEnter={() => previewJewelryGroup(group)}
                            onClick={() => {
                              if (isDirectMaterial) {
                                handleFilterRedirect(group.materialValue || "", "", "");
                              }
                            }}
                          >
                            <span className="metal-label">{group.label}</span>
                            {!isDirectMaterial ? (
                              <span className="metal-arrow">{">"}</span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {activeJewelryGroup?.behavior === "nested-materials" ? (
                    <div className="jewelry-level-box jewelry-level-box--submenu">
                      <div className="jewelry-level-heading">
                        {`JEWELRY METAL / ${activeDesktopJewelryGroupLabel}`}
                      </div>
                      <div className="jewelry-level-list">
                        {activeDesktopJewelryChildren.map((child) => (
                          <div
                            key={child.id}
                            className={`dropdown-item jewelry-type-item${activeJewelryChild?.id === child.id ? " is-active" : ""}`}
                            onMouseEnter={() => previewJewelryChild(child)}
                          >
                            <span>{child.label}</span>
                            <span className="metal-arrow">{">"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {activeJewelryGroup?.behavior === "product-types" ? (
                    <div className="jewelry-level-box jewelry-level-box--products">
                      <div className="jewelry-level-heading">
                        {`JEWELRY TYPE / ${activeDesktopJewelryGroupLabel}`}
                      </div>
                      <div className="jewelry-level-list jewelry-level-list--products">
                        {jewelryProducts.map((product) => (
                          <div
                            key={`${activeJewelryGroup.id}-${product}`}
                            className="dropdown-item jewelry-type-item"
                            onClick={() =>
                              handleFilterRedirect(
                                activeDesktopJewelryMaterialValue,
                                product,
                                ""
                              )
                            }
                          >
                            {product}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {shouldShowDesktopJewelryProducts && activeJewelryGroup?.behavior === "nested-materials" ? (
                    <div className="jewelry-level-box jewelry-level-box--products">
                      <div className="jewelry-level-heading">
                        {`JEWELRY TYPE / ${activeDesktopJewelryChildLabel}`}
                      </div>
                      <div className="jewelry-level-list jewelry-level-list--products">
                        {jewelryProducts.map((product) => (
                          <div
                            key={`${activeJewelryChild?.id || activeJewelryGroup?.id}-${product}`}
                            className="dropdown-item jewelry-type-item"
                            onClick={() =>
                              handleFilterRedirect(
                                activeDesktopJewelryMaterialValue,
                                product,
                                ""
                              )
                            }
                          >
                            {product}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          {/* GEMSTONES - single-panel dropdown (same as metals) */}
          <div
            className="menu-item gemstone-single-panel-wrapper"
            onMouseEnter={(e) => {
              clearGemstoneCloseTimeout();
              positionDropdown("gemstones", e.currentTarget);
              setIsGemstoneOpen(true);
            }}
            onMouseLeave={() => {
              gemstoneCloseTimeoutRef.current = setTimeout(() => {
                closeGemstoneDropdown();
              }, 120);
            }}
          >
            <span className="menu-item-label">GEMSTONES <IoIosArrowDown size={14} /></span>
            {isGemstoneOpen && (
              <div
                className="gemstone-single-panel-container"
                style={{
                  top: dropdownTop,
                  left: dropdownLeft.gemstones,
                  minWidth: gemstonePanelMinWidth ? `${gemstonePanelMinWidth}px` : undefined,
                }}
                ref={gemstonePanelRef}
                onMouseEnter={clearGemstoneCloseTimeout}
                onMouseLeave={() => {
                  gemstoneCloseTimeoutRef.current = setTimeout(() => {
                    closeGemstoneDropdown();
                  }, 120);
                }}
              >
                {selectedGemType == null ? (
                  /* Step 1: Gem types */
                  <div className="gemstone-panel gemstone-types-only">
                    <div className="jewelry-dropdown-heading">GEMSTONE TYPE</div>
                    <div className="jewelry-dropdown-items gemstone-types-list">
                      {gemStoneTypes.map((type, i) => (
                        <div
                          key={i}
                          className="dropdown-item jewelry-metal-item"
                          onClick={() => setSelectedGemType(type)}
                        >
                          <span className="metal-label">{type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : selectedGemstone == null ? (
                  /* Step 2: Gemstones for selected type */
                  <div className="gemstone-panel gemstone-gems-only">
                    <div className="jewelry-dropdown-heading jewelry-dropdown-heading-with-back">
                      <button type="button" className="jewelry-back-btn" onClick={(e) => { e.stopPropagation(); setSelectedGemType(null); }} aria-label="Back to types">{'< Back'}</button>
                      <span>GEMSTONE TYPE{selectedGemType && ` / ${selectedGemType}`}</span>
                    </div>
                    {isSemiPreciousGemType ? (
                      <div className="jewelry-dropdown-items gemstone-six-column-layout">
                        {semiPreciousGemColumns.map((column, columnIndex) => (
                          <div key={columnIndex} className="gemstone-six-column">
                            {column.map((gem) => (
                              <div
                                key={gem}
                                className="dropdown-item jewelry-type-item"
                                onClick={() => setSelectedGemstone(gem)}
                              >
                                {gem}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="jewelry-dropdown-items jewelry-types-list">
                        {selectedGemOptions.map((gem, j) => (
                          <div
                            key={j}
                            className="dropdown-item jewelry-type-item"
                            onClick={() => setSelectedGemstone(gem)}
                          >
                            {gem}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Step 3: Shapes - click to redirect */
                  <div className="gemstone-panel gemstone-shapes-only">
                    <div className="jewelry-dropdown-heading jewelry-dropdown-heading-with-back">
                      <button type="button" className="jewelry-back-btn" onClick={(e) => { e.stopPropagation(); setSelectedGemstone(null); }} aria-label="Back to gemstones">{'< Back'}</button>
                      <span>GEMSTONE TYPE{selectedGemType && selectedGemstone && ` / ${selectedGemType} / ${selectedGemstone}`}</span>
                    </div>
                    <div className="jewelry-dropdown-items jewelry-types-list">
                      {gemstoneShapes.map((shape, k) => (
                        <div
                          key={k}
                          className="dropdown-item jewelry-type-item"
                          onClick={() => handleGemstoneRedirect(selectedGemType, selectedGemstone, shape)}
                        >
                          {shape}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SPIRITUAL COLLECTION - same style as jewelry & gemstone */}
          <div
            className="menu-item spiritual-single-panel-wrapper"
            onMouseEnter={(e) => {
              clearSpiritualCloseTimeout();
              positionDropdown("spiritual", e.currentTarget);
              setIsSpiritualOpen(true);
            }}
            onMouseLeave={() => {
              spiritualCloseTimeoutRef.current = setTimeout(() => {
                closeSpiritualDropdown();
              }, 120);
            }}
          >
            <span className="menu-item-label">SPIRITUAL COLLECTION <IoIosArrowDown size={14} /></span>
            {isSpiritualOpen && (
              <div
                className="spiritual-single-panel-container"
                style={{ top: dropdownTop, left: dropdownLeft.spiritual, transform: 'translateX(-50%)' }}
                ref={spiritualPanelRef}
                onMouseEnter={clearSpiritualCloseTimeout}
                onMouseLeave={() => closeSpiritualDropdown()}
              >
                {selectedSpiritualItem == null ? (
                  <div className="spiritual-panel">
                    <div className="jewelry-dropdown-heading">SPIRITUAL COLLECTION</div>
                    <div className="jewelry-dropdown-items spiritual-items-list">
                      {SPIRITUAL_MENU_ITEMS.map((item) => (
                        <div
                          key={item.key}
                          className={`dropdown-item jewelry-type-item${item.children ? ' spiritual-parent-item' : ''}`}
                          onClick={() => {
                            if (item.children) {
                              setSelectedSpiritualItem(item.key);
                            } else {
                              handleFilterRedirect(item.material, '', '');
                            }
                          }}
                        >
                          <span>{item.label}</span>
                          {item.children ? <span className="spiritual-parent-arrow">{'>'}</span> : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="spiritual-panel">
                    <div className="jewelry-dropdown-heading jewelry-dropdown-heading-with-back">
                      <button
                        type="button"
                        className="jewelry-back-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSpiritualItem(null);
                        }}
                        aria-label="Back to spiritual collection"
                      >
                        {'< Back'}
                      </button>
                      <span>SPIRITUAL COLLECTION / {selectedSpiritualItem}</span>
                    </div>
                    <div className="jewelry-dropdown-items spiritual-items-list">
                      {(findSpiritualMenuItem(selectedSpiritualItem)?.children || []).map((item) => (
                        <div
                          key={item.key}
                          className="dropdown-item jewelry-type-item"
                          onClick={() => handleFilterRedirect(item.material, '', '')}
                        >
                          {item.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <Link to="/bespoke-jewelry" className="menu-item">BESPOKE JEWELRY</Link>
          <Link to="/contact" className="menu-item">CONTACT</Link>
          <Link to="/more" className="menu-item" onClick={handleMoreFeatureClick}>MORE AT PANCHALI JEWELS</Link>
        </nav>

        {/* Mobile Menu */}
        <nav className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          {/* Mobile Navigation - Only show dropdown categories and HOME */}
          <div className="mobile-nav-items">
            <Link to="/" className="mobile-menu-item" onClick={() => setIsMobileMenuOpen(false)}>
              HOME
            </Link>

            {/* Mobile JEWELRY METAL Dropdown */}
            <div className="mobile-menu-item mobile-dropdown">
              <div
                className="mobile-dropdown-header"
                onClick={() => {
                  toggleMobileDropdown('jewelry');
                  resetMobileJewelryPath();
                }}
              >
                JEWELRY METAL <IoIosArrowDown size={14} className={isMobileDropdownOpen.jewelry ? 'rotated' : ''} />
              </div>
              {isMobileDropdownOpen.jewelry && (
                <div className="mobile-dropdown-content">
                  {activeMobileJewelryGroup == null ? (
                    <>
                      <div className="mobile-dropdown-section-heading">Jewelry Metal</div>
                      {jewelryMenuGroups.map((group) => (
                        <div
                          key={group.id}
                          className="mobile-dropdown-item mobile-dropdown-item-arrow"
                          onClick={() => openMobileJewelryGroup(group)}
                        >
                          {group.label}
                          {group.behavior !== "direct-material" ? (
                            <span style={{ float: 'right', color: '#51b9ed' }}>{'>'}</span>
                          ) : null}
                        </div>
                      ))}
                    </>
                  ) : activeMobileJewelryGroup.behavior === "nested-materials" && activeMobileJewelryChild == null ? (
                    <>
                      <div
                        className="mobile-dropdown-item mobile-back-item"
                        onClick={resetMobileJewelryPath}
                      >
                        {'< Back'}
                      </div>
                      <div className="mobile-dropdown-section-heading" style={{ fontSize: 11 }}>
                        {`JEWELRY METAL / ${String(activeMobileJewelryGroup.label || '').toUpperCase()}`}
                      </div>
                      {(activeMobileJewelryGroup.children || []).map((child) => (
                        <div
                          key={child.id}
                          className="mobile-dropdown-item mobile-dropdown-item-arrow"
                          onClick={() => openMobileJewelryChild(child)}
                        >
                          {child.label}
                          <span style={{ float: 'right', color: '#51b9ed' }}>{'>'}</span>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      <div
                        className="mobile-dropdown-item mobile-back-item"
                        onClick={() => {
                          if (activeMobileJewelryGroup?.behavior === "nested-materials") {
                            setActiveMobileJewelryChildId(null);
                          } else {
                            setActiveMobileJewelryGroupId(null);
                          }
                        }}
                      >
                        {'< Back'}
                      </div>
                      <div className="mobile-dropdown-section-heading" style={{ fontSize: 11 }}>
                        {`JEWELRY TYPE / ${String(
                          activeMobileJewelryChild?.label || activeMobileJewelryGroup?.label || ''
                        ).toUpperCase()}`}
                      </div>
                      {jewelryProducts.map((product) => (
                        <div
                          key={`${activeMobileJewelryChild?.id || activeMobileJewelryGroup?.id}-${product}`}
                          className="mobile-dropdown-item"
                          onClick={() =>
                            handleFilterRedirect(
                              activeMobileJewelryChild?.materialValue || activeMobileJewelryGroup?.materialValue || '',
                              product,
                              ''
                            )
                          }
                        >
                          {product}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Mobile GEMSTONES Dropdown - 3-step */}
            <div className="mobile-menu-item mobile-dropdown">
              <div className="mobile-dropdown-header" onClick={() => { toggleMobileDropdown('gemstones'); setActiveMobileGemType(null); setActiveMobileGemstone(null); }}>
                GEMSTONES <IoIosArrowDown size={14} className={isMobileDropdownOpen.gemstones ? 'rotated' : ''} />
              </div>
              {isMobileDropdownOpen.gemstones && (
                <div className="mobile-dropdown-content">
                  {/* Step 1: gem types */}
                  {activeMobileGemType === null && (
                    <>
                      <div className="mobile-dropdown-section-heading">Select Gemstone Type</div>
                      {gemStoneTypes.map((type, i) => (
                        <div key={i} className="mobile-dropdown-item mobile-dropdown-item-arrow" onClick={() => setActiveMobileGemType(type)}>
                          {type} <span style={{ float: 'right', color: '#51b9ed' }}>{'>'}</span>
                        </div>
                      ))}
                    </>
                  )}
                  {/* Step 2: specific gemstones */}
                  {activeMobileGemType !== null && activeMobileGemstone === null && (
                    <>
                      <div className="mobile-dropdown-item mobile-back-item" onClick={() => setActiveMobileGemType(null)}>{'< Back'}</div>
                      <div className="mobile-dropdown-section-heading" style={{ fontSize: 11 }}>{activeMobileGemType}</div>
                      <div className="mobile-dropdown-section-heading">Select Gemstone</div>
                      {(gemstoneOptions[activeMobileGemType] || []).map((gem, j) => (
                        <div key={j} className="mobile-dropdown-item mobile-dropdown-item-arrow" onClick={() => setActiveMobileGemstone(gem)}>
                          {gem} <span style={{ float: 'right', color: '#51b9ed' }}>{'>'}</span>
                        </div>
                      ))}
                    </>
                  )}
                  {/* Step 3: shapes */}
                  {activeMobileGemType !== null && activeMobileGemstone !== null && (
                    <>
                      <div className="mobile-dropdown-item mobile-back-item" onClick={() => setActiveMobileGemstone(null)}>{'< Back'}</div>
                      <div className="mobile-dropdown-section-heading" style={{ fontSize: 11 }}>{activeMobileGemstone}</div>
                      <div className="mobile-dropdown-section-heading">Choose Shape</div>
                      {gemstoneShapes.map((shape, k) => (
                        <div key={k} className="mobile-dropdown-item" onClick={() => handleGemstoneRedirect(activeMobileGemType, activeMobileGemstone, shape)}>
                          {shape}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Mobile SPIRITUAL COLLECTION Dropdown */}
            <div className="mobile-menu-item mobile-dropdown">
              <div
                className="mobile-dropdown-header"
                onClick={() => {
                  toggleMobileDropdown('spiritual');
                  setActiveMobileSpiritualItem(null);
                }}
              >
                SPIRITUAL COLLECTION <IoIosArrowDown size={14} className={isMobileDropdownOpen.spiritual ? 'rotated' : ''} />
              </div>
              {isMobileDropdownOpen.spiritual && (
                <div className="mobile-dropdown-content">
                  {activeMobileSpiritualItem === null ? (
                    <>
                      <div className="mobile-dropdown-section-heading">Select Spiritual Collection</div>
                      {SPIRITUAL_MENU_ITEMS.map((item) => (
                        <div
                          key={item.key}
                          className={`mobile-dropdown-item${item.children ? ' mobile-dropdown-item-arrow' : ''}`}
                          onClick={() => {
                            if (item.children) {
                              setActiveMobileSpiritualItem(item.key);
                            } else {
                              handleFilterRedirect(item.material, '', '');
                            }
                          }}
                        >
                          {item.label}
                          {item.children ? <span style={{ float: 'right', color: '#51b9ed' }}>{'>'}</span> : null}
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      <div
                        className="mobile-dropdown-item mobile-back-item"
                        onClick={() => setActiveMobileSpiritualItem(null)}
                      >
                        {'< Back'}
                      </div>
                      <div className="mobile-dropdown-section-heading" style={{ fontSize: 11 }}>
                        {activeMobileSpiritualItem}
                      </div>
                      <div className="mobile-dropdown-section-heading">Select Coanch Type</div>
                      {(findSpiritualMenuItem(activeMobileSpiritualItem)?.children || []).map((item) => (
                        <div
                          key={item.key}
                          className="mobile-dropdown-item"
                          onClick={() => handleFilterRedirect(item.material, '', '')}
                        >
                          {item.label}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Hamburger Menu Items */}
          <div className="hamburger-menu-items">
            <Link to="/about" className="mobile-menu-item" onClick={() => setIsMobileMenuOpen(false)}>
              ABOUT US
            </Link>
            <Link to="/bespoke-jewelry" className="mobile-menu-item" onClick={() => setIsMobileMenuOpen(false)}>
              BESPOKE JEWELRY
            </Link>
            <Link to="/contact" className="mobile-menu-item" onClick={() => setIsMobileMenuOpen(false)}>
              CONTACT
            </Link>
            <Link to="/more" className="mobile-menu-item" onClick={handleMoreFeatureClick}>
              MORE AT PANCHALI JEWELS
            </Link>
          </div>
        </nav>

        {/* Mobile Search Overlay */}
        {isMobileSearchOpen && (
          <div className="mobile-search-overlay">
            <form className="mobile-search-form" onSubmit={handleSearch}>
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                autoFocus
              />
              <button type="submit">
                <FaSearch />
              </button>
              <button 
                type="button" 
                className="close-search"
                onClick={() => setIsMobileSearchOpen(false)}
              >
                <FaTimes />
              </button>
            </form>
          </div>
        )}

        {isMoreNoticeOpen && (
          <div
            className="coming-soon-overlay"
            onClick={() => setIsMoreNoticeOpen(false)}
            role="presentation"
          >
            <div
              className="coming-soon-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="coming-soon-title"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="coming-soon-close"
                onClick={() => setIsMoreNoticeOpen(false)}
                aria-label="Close coming soon message"
              >
                ×
              </button>
              <div className="coming-soon-kicker">MORE AT PANCHALI JEWELS</div>
              <h3 id="coming-soon-title">Coming Soon</h3>
              <p>
                Kindly come back later. This feature will be part of our future updates at Panchali Jewels.
              </p>
              <button
                type="button"
                className="coming-soon-button"
                onClick={() => setIsMoreNoticeOpen(false)}
              >
                Okay
              </button>
            </div>
          </div>
        )}
      
    </header>
    </div>
  );
};

export default Navbar;

